import useAssessmentTemplateWithQuestions from '@app/src/pages/Configurations/ConfigurationsPages/Automation/Hooks/useAssessmentTemplateWithQuestions'
import { getScoringOptionValues } from '@app/src/utils/assessmentRules'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import OptionRow from './OptionRow'
import QuestionRow from './QuestionRow'
import SectionRow from './SectionRow'
import {
  countSelectedKpis,
  isOptionStarred,
  isQuestionStarred,
  isSectionStarred,
  toggleOption,
  toggleQuestion,
  toggleSection,
} from './importantKpisLogic'
import type { ImportantKpiEntry } from './types'

const toggleExpanded = (set: Set<number>, id: number): Set<number> => {
  const next = new Set(set)
  next.has(id) ? next.delete(id) : next.add(id)
  return next
}

type HighlightImportantKpisProps = {
  fieldName: string
  assessmentTemplateId: number | undefined
}

const HighlightImportantKpis: React.FC<HighlightImportantKpisProps> = ({ fieldName, assessmentTemplateId }) => {
  const { watch, setValue } = useFormContext()
  const entries: ImportantKpiEntry[] = watch(fieldName) ?? []

  const { assessmentTemplate, isLoading } = useAssessmentTemplateWithQuestions(assessmentTemplateId)
  const sections = assessmentTemplate?.sections ?? []
  const allRules = useMemo(() => sections.flatMap(s => s.rules ?? []), [sections])

  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set())
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set())

  const setEntries = (next: ImportantKpiEntry[]) => setValue(fieldName, next, { shouldDirty: true })

  if (isLoading || assessmentTemplateId == null) {
    return (
      <Stack spacing={1}>
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} variant="rectangular" height={48} />
        ))}
      </Stack>
    )
  }

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      {sections.map((section, index) => (
        <SectionRow
          key={section.id}
          section={section}
          status={isSectionStarred(entries, section, allRules)}
          isExpanded={expandedSections.has(section.id)}
          onToggleExpand={() => setExpandedSections(prev => toggleExpanded(prev, section.id))}
          onToggleStar={() => setEntries(toggleSection(entries, section, allRules))}
          isFirst={index === 0}
        >
          {(section.questions ?? []).map((aq, qIndex) => {
            const scoringOptions = getScoringOptionValues(aq.question, allRules)
            const expandable = scoringOptions.length > 1
            return (
              <QuestionRow
                key={aq.question.id}
                question={aq.question}
                status={isQuestionStarred(entries, aq.question, allRules)}
                isExpanded={expandedQuestions.has(aq.question.id)}
                onToggleExpand={() => setExpandedQuestions(prev => toggleExpanded(prev, aq.question.id))}
                onToggleStar={() => setEntries(toggleQuestion(entries, aq.question, allRules))}
                isFirst={qIndex === 0}
                showChevron={expandable}
              >
                {scoringOptions.map(value => (
                  <OptionRow
                    key={value}
                    optionLabel={
                      aq.question.requestCollection?.items?.find(item => item.value === value)?.displayText ?? value
                    }
                    isStarred={isOptionStarred(entries, aq.question.id, value)}
                    onToggleStar={() => setEntries(toggleOption(entries, aq.question, value))}
                  />
                ))}
              </QuestionRow>
            )
          })}
        </SectionRow>
      ))}
    </Box>
  )
}

export default HighlightImportantKpis

export const useImportantKpisFooterContent = (fieldName: string) => {
  const { formatMessage } = useIntl()
  const { watch } = useFormContext()
  const entries: ImportantKpiEntry[] = watch(fieldName) ?? []
  const count = countSelectedKpis(entries)
  if (count === 0) return null
  return (
    <Typography variant="body2" color="textSecondary">
      {formatMessage({ id: 'form.createAutomation.highlightImportantKpis.selectedCount' }, { count })}
    </Typography>
  )
}
